import React from "react";
import { useState, useEffect, useRef } from "react";
import type { ReactNode } from "react";
import dayjs from 'dayjs';
import { Table, Button, Modal, Input, Space, Select, Flex, Switch, DatePicker, message, Skeleton, theme } from "antd";
import type {
    ColumnsType,
    TablePaginationConfig,
    TableProps,
} from "antd/es/table";
import type { SortOrder, FilterValue, Key } from "antd/es/table/interface"; // Import Key
import { useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
    DeleteOutlined,
    EditOutlined,
    // EyeOutlined,
    ExclamationCircleOutlined,
    CheckCircleOutlined,
    SearchOutlined,
    ReloadOutlined, // Import ReloadOutlined for the clear button
} from "@ant-design/icons";

import { del, post, get } from '@/common/services/httpClient';
import { useAppMutation } from "@/common/hooks/useAppMutation";
import { useAppQuery } from "@/common/hooks/useAppQuery";
import { hasPermission } from '@/common/services/permissionService'; // Import hasPermission
import { getDynamicTableActionPermissions } from '@/common/utils/dynamicTablePermissions'; // Import the new utility
import Export from "../Export";
import { getPermissionKeyFromApi } from '@/common/components/fromatPermission';

export interface ItemType {
    id: string | number;
    [key: string]: unknown;
}

type QueryParams = {
    pageNumber: number;
    pageSize: number;
    sortField?: string;
    sortOrder?: SortOrder;
    filters?: { [key: string]: FilterValue | null | string };
};

type FetchResponse<T extends ItemType> = {
    items: T[];
    total: number;
};

type FilterConfig = {
    field: string;
    placeholder?: string;
    type?: 'input' | 'select' | 'switch' | 'date' | 'export'; // Type of filter    
    required?: boolean;
    requiredMessage?: string;
    options?: { label: string; value: string | number }[]; // Static options for select
    defaultValue?: string | number | boolean | null; // <-- Add this line
    displayFormat?: string; // Display format for date fields (e.g., 'DD-MM-YYYY'), defaults to 'YYYY-MM-DD'
    showSearch?: boolean; // For searchable select
    dynamicOptions?: { // For options fetched from API
        url: string;
        method?: 'GET' | 'POST';
        body?: Record<string, unknown>;
        transformData: (apiResponse: unknown) => { label: string; value: string | number }[];
    };
    exportOptions?: {
        url: string;
        fileName: string;
        fileType?: string;
        defaultPayload?: Record<string, unknown>;
        columns?: {
            header: string;
            key: string;
            transform?: (val: any) => any;
        }[];
    };
};

type Action<T> = {
    render: (record: T) => ReactNode;
};

type DynamicTableProps<T extends ItemType> = {
    dataApiUrl?: string; // Made optional
    deleteBaseUrl?: string;
    deletePayloadField?: string;
    deleteSecondQueryKey?: string | unknown[];
    editBaseUrl?: string | ((record: T) => string) | null;
    columns?: ColumnsType<T>;
    onEdit?: (id: string | number) => void;
    baseQueryKey: string[];
    resourceName?: string; // New prop for resource name
    showActions?:
    | boolean
    | {
        edit?: boolean | string | ((record: T) => boolean); // Added string type
        view?: boolean | string | ((record: T) => boolean);   // 
        delete?: boolean | string | ((record: T) => boolean); // Added string type
    };
    showAddNew?: boolean;
    filterConfigs?: FilterConfig[];
    addNewButtonText?: string;
    addNewPermission?: string; // New prop for add new button permission
    searchPermission?: string; // New prop for search permission
    secondButtonTitle?: string;
    secondButtonUrl?: string;
    secondButtonPermissionKey?: string;
    tableTitle?: string | React.ReactNode;
    customActions?: Action<T>[];
    actionColumnWidth?: number;
    actionVisibility?: ((record: T) => boolean) | string | boolean;
    rowSelectionEnabled?: boolean;
    rowSelectable?: (record: T) => boolean;
    onRowSelectionChange?: (selectedRowKeys: Key[], selectedRows: T[]) => void; // Changed to Key[]
    selectedRowKeys?: Key[]; // Changed to Key[]
    headerActions?: ReactNode[];
    expandable?: {
        expandedRowRender: (record: T, index: number, indent: number, expanded: boolean) => React.ReactNode;
        rowExpandable?: (record: T) => boolean;
    };
    initialFilters?: Record<string, FilterValue | null | string | number>;
    extraParameters?: Record<string, number | string | FilterValue | null | undefined>;
};

const DynamicTable = <T extends ItemType>({
    dataApiUrl,
    deleteBaseUrl,
    deletePayloadField,
    deleteSecondQueryKey,
    editBaseUrl = "form",
    onEdit,
    columns: customColumns,
    baseQueryKey,
    resourceName: propResourceName, // Renamed to avoid conflict
    showActions: propShowActions = true, // Renamed to avoid conflict
    showAddNew = true,
    filterConfigs = [],
    addNewButtonText,
    addNewPermission: propAddNewPermission, // Renamed to avoid conflict
    searchPermission: propSearchPermission, // Destructure new prop
    secondButtonTitle,
    secondButtonUrl,
    secondButtonPermissionKey,
    tableTitle,
    customActions = [],
    actionColumnWidth = 150,
    actionVisibility,
    rowSelectionEnabled = false,
    onRowSelectionChange,
    rowSelectable = () => true,
    selectedRowKeys: controlledSelectedRowKeys,
    headerActions,
    expandable,
    initialFilters,
    // Added a comment to trigger re-evaluation
    extraParameters,
}: DynamicTableProps<T>) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const tableContainerRef = useRef<HTMLDivElement | null>(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [deleteTarget, setDeleteTarget] = useState<{ id: string | number; entityReference?: string } | null>(null);
    const [dropdownOptions, setDropdownOptions] = useState<Record<string, { label: string; value: string | number }[]>>({});

    const [pagination, setPagination] = useState<TablePaginationConfig>({
        current: 1,
        pageSize: 10,
    });

    const [sorter, setSorter] = useState<{
        field?: string;
        order?: SortOrder;
    }>({});

    // Derive resourceName from dataApiUrl if not explicitly provided
    const derivedResourceName = propResourceName || (dataApiUrl ? dataApiUrl.split('/')[0] : undefined);

    // Determine effective showActions, addNewPermission, and searchPermission
    const effectivePermissions = derivedResourceName ? getDynamicTableActionPermissions<T>(derivedResourceName) : undefined;

    const showActions = propShowActions === true && effectivePermissions
        ? effectivePermissions.showActions
        : propShowActions;

    const addNewPermission = propAddNewPermission || (effectivePermissions ? effectivePermissions.addNewPermission : undefined);
    const searchPermission = propSearchPermission || (effectivePermissions ? effectivePermissions.searchPermission : undefined);

    // Conditionally set dataApiUrl based on searchPermission
    const effectiveDataApiUrl = (searchPermission ? hasPermission(searchPermission) : true) ? dataApiUrl : undefined;

    // State to hold the values currently typed in the individual input fields
    const [localInputValues, setLocalInputValues] = useState<Record<string, string | number | boolean | null>>(() => {
        const initialLocalValues: Record<string, string | number | boolean | null> = {};
        filterConfigs.forEach(config => {
            initialLocalValues[config.field] = config.defaultValue === undefined ? (config.type === 'select' ? null : "") : config.defaultValue;
            // initialLocalValues[config.field] = config.defaultValue ?? (config.type === 'select' ? null : "");
        });
        return initialLocalValues;
    });

    // State to hold the *active* filters that trigger data fetching
    const [activeFilters, setActiveFilters] = useState<Record<string, FilterValue | null>>(() => {
        const initialActiveFilters: Record<string, FilterValue | null> = {};
        filterConfigs.forEach(config => {
            if (config.defaultValue !== undefined) {
                initialActiveFilters[config.field] = config.defaultValue === null ? null : [config.defaultValue];
            } else {
                initialActiveFilters[config.field] = config.field.endsWith('Id') ? [0] : [""];
            }
        });
        // Merge with initialFilters if provided
        if (initialFilters) {
            Object.entries(initialFilters).forEach(([key, value]) => {
                initialActiveFilters[key] = (Array.isArray(value) || value === null) ? value : [value];
            });
        }
        return initialActiveFilters;
    });

    const [internalSelectedRowKeys, setInternalSelectedRowKeys] = useState<Key[]>([]); // Changed to Key[]

    const actualSelectedRowKeys = controlledSelectedRowKeys !== undefined ? controlledSelectedRowKeys : internalSelectedRowKeys;

    // Sync localInputValues and activeFilters when filterConfigs prop changes
    useEffect(() => {
        setLocalInputValues(prevLocalValues => {
            const newLocalValues = { ...prevLocalValues };
            let changed = false;
            filterConfigs.forEach(config => {
                if (!(config.field in newLocalValues)) {
                    newLocalValues[config.field] = config.type === 'select' ? null : "";
                    changed = true;
                }
            });
            Object.keys(newLocalValues).forEach(key => {
                if (!filterConfigs.some(config => config.field === key)) {
                    delete newLocalValues[key];
                    changed = true;
                }
            });
            return changed ? newLocalValues : prevLocalValues;
        });

        setActiveFilters(prevActiveFilters => {
            const newActiveFilters = { ...prevActiveFilters };
            let changed = false;
            filterConfigs.forEach(config => {
                if (!(config.field in newActiveFilters)) {
                    newActiveFilters[config.field] = config.field.endsWith('Id') ? [0] : [""];
                    changed = true;
                }
            });
            Object.keys(newActiveFilters).forEach(key => {
                if (!filterConfigs.some(config => config.field === key)) {
                    delete newActiveFilters[key];
                    changed = true;
                }
            });
            return changed ? newActiveFilters : prevActiveFilters;
        });
    }, [filterConfigs]);

    useEffect(() => {

        if (initialFilters) {

            setActiveFilters(prev => {

                const newFilters = { ...prev };

                Object.entries(initialFilters).forEach(([key, value]) => {

                    newFilters[key] = Array.isArray(value) || value === null ? value : [value];

                });

                return newFilters;

            });



            // also reset pagination when filters change

            setPagination(prev => ({ ...prev, current: 1 }));

        }

    }, [initialFilters]);
    useEffect(() => {
        const fetchOptions = async () => {
            const newOptions: Record<string, { label: string; value: string | number }[]> = {};
            for (const config of filterConfigs) {
                if (config.type === 'select' && config.dynamicOptions) {
                    try {
                        // const response = await post(config.dynamicOptions.url, config.dynamicOptions.body || {});
                        const response = config.dynamicOptions.method === 'GET'
                            ? await get(config.dynamicOptions.url, config.dynamicOptions.body || {})
                            : await post(config.dynamicOptions.url, config.dynamicOptions.body || {});
                        const transformedData = config.dynamicOptions.transformData(response);
                        newOptions[config.field] = transformedData;
                    } catch (error) {
                        console.error(`Failed to fetch options for ${config.field}:`, error);
                        newOptions[config.field] = [];
                    }
                }
            }
            setDropdownOptions(newOptions);
        };

        fetchOptions();
    }, [filterConfigs]);

    const router = useRouter();

    const fetchTableData = async ({
        pageNumber,
        pageSize,
        sortField,
        sortOrder,
        filters,
    }: QueryParams): Promise<FetchResponse<T>> => {
        if (!effectiveDataApiUrl) { // Use effectiveDataApiUrl here
            return { items: [], total: 0 };
        }

        const bodyPayload: {
            pageNumber: number;
            pageSize: number;
            sortColumn?: string;
            sortDirection?: string;
            [key: string]: number | string | FilterValue | null | undefined;
        } = {
            pageNumber: pageNumber,
            pageSize,
            ...extraParameters,
        };

        if (sortField) {
            bodyPayload.sortColumn = sortField;
            if (sortOrder) {
                bodyPayload.sortDirection = sortOrder === "ascend" ? "asc" : "desc";
            }
        }

        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (Array.isArray(value) && value.length > 0) {
                    let val = value[0];

                    // find matching filter config
                    const config = filterConfigs.find(f => f.field === key);

                    // convert empty string to null for date fields
                    if (config?.type === 'date' && (val === "" || val === undefined)) {
                        bodyPayload[key] = null;
                    }

                    if (typeof val === 'string' || typeof val === 'number' || val === null || val == undefined) {
                        bodyPayload[key] = val;
                    }

                } else if (value !== null && value !== undefined) {
                    bodyPayload[key] = value;
                }
            });
        }

        const rawApiResponse = await post<{ data: T[], paging: { totalCount: number } }>(effectiveDataApiUrl, bodyPayload); // Use effectiveDataApiUrl here
        return {
            items: rawApiResponse.data || [],
            total: rawApiResponse.paging ? rawApiResponse.paging.totalCount : 0,
        };
    };

    const dynamicQueryKey = [
        ...baseQueryKey,
        {
            url: effectiveDataApiUrl, // Use effectiveDataApiUrl here
            page: pagination.current,
            pageSize: pagination.pageSize,
            sort: sorter,
            filters: activeFilters, // Use activeFilters here to trigger queries
        },
    ];

    const { data, isLoading, isFetching, error } = useAppQuery<FetchResponse<T>, Error, FetchResponse<T>, typeof dynamicQueryKey>(dynamicQueryKey,
        () => {
            const currentPage = pagination.current ?? 1;
            const currentSize = pagination.pageSize ?? 10;
            return fetchTableData({
                pageNumber: currentPage,
                pageSize: currentSize,
                sortField: sorter.field,
                sortOrder: sorter.order,
                filters: activeFilters,
            }).then(response => {
                return response;
            });
        },
        {
            placeholderData: (previousData: FetchResponse<T> | undefined) => previousData ? previousData : undefined,
            // staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false

        }
    );

    const performDelete = async (payload: { id: number | string; entityReference?: string }) => {
        const actualDeleteUrl = deleteBaseUrl
            ? `${deleteBaseUrl}?id=${payload.id}${payload.entityReference ? `&entityReference=${payload.entityReference}` : ''}`
            : ``;
        console.log(`Attempting to delete item with ID: ${payload.id} from ${actualDeleteUrl} using httpClient.delete`);
        const response = await del<{ isError: boolean; message: string }>(actualDeleteUrl, { data: payload });
        console.log(`Delete request sent for item ${payload.id}. Response:`, response);

        if (response.isError) {
            throw new Error(response.message || t("An unknown error occurred during deletion"));
        }

        return response;
    };

    const deleteMutation = useAppMutation(
        (payload: { id: number | string; entityReference?: string }) => performDelete(payload),
        {
            //successMessage: "Deleted successfully",
            errorMessagePrefix: t("Delete failed"),
            invalidateQueryKey: baseQueryKey,
            SecondQueryKey: deleteSecondQueryKey,

            onSuccess: (response) => {
                setSuccessMessage(response?.message || t('common.deletedSuccessfully', { defaultValue: 'Deleted Successfully' }));
                setSuccessModalOpen(true);
            },
        }
    );

    const handleDelete = (record: { id: string | number;[key: string]: any }) => {
        const payload: { id: string | number; entityReference?: string } = {
            id: record.id,
        };

        if (deletePayloadField) {
            const fieldValue = record[deletePayloadField];
            if (fieldValue !== undefined && fieldValue !== null) {
                payload.entityReference = String(fieldValue);
            }
        }

        setDeleteTarget(payload);
        setDeleteModalOpen(true);
    };

    const handleGlobalSearch = () => {

        // --- Validate Required Filters ---
        // Find the first required filter that is missing and show its specific message when provided
        const missingConfig = filterConfigs?.find(cfg => cfg.required && (() => {
            const val = localInputValues[cfg.field];
            // Treat empty string, null, undefined, or empty array as missing
            if (cfg.type === 'switch') {
                return val === null || val === undefined; // switch uses 0/1 or null
            }
            if (Array.isArray(val)) {
                return val.length === 0;
            }
            if (typeof val === 'string') {
                return val.trim() === '';
            }
            return val === null || val === undefined;
        })());

        if (missingConfig) {
            const msg = missingConfig.requiredMessage || t('validation.selectRequiredFields') || "Please select all required fields before applying filters.";
            message.error(msg);
            return; // stop here if required filters missing
        }

        setActiveFilters(prevActiveFilters => {
            const newActiveFilters = { ...prevActiveFilters };
            filterConfigs.forEach(config => {
                const value = localInputValues[config.field];
                const trimmedValue = typeof value === "string" ? value.trim() : value;
                // For cleared/empty filters, fields ending with 'Id' should default to 0,
                // while others default to an empty string. A value of 0 is still treated as a valid filter.
                newActiveFilters[config.field] = (trimmedValue !== null && trimmedValue !== undefined && trimmedValue !== "")
                    ? [trimmedValue]
                    : (config.defaultValue === null ? null : (config.field.endsWith('Id') ? [0] : [""]));
            });
            return newActiveFilters;
        });
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const buildExtraPayload = () => {
        const payload: Record<string, any> = {};

        filterConfigs.forEach(config => {
            // Skip export-only fields (Excel etc.)
            if (config.type === "export") return;

            const value = localInputValues[config.field];
            payload[config.field] = value !== null && value !== undefined
                ? value
                : (config.defaultValue === null ? null : (config.field.endsWith("Id") ? 0 : "")); // default
        });

        return payload;
    };


    const handleClearFilters = () => {
        setLocalInputValues(() => {
            const clearedLocalValues: Record<string, string | number | null | boolean> = {};
            filterConfigs.forEach(config => {
                clearedLocalValues[config.field] = config.defaultValue === undefined
                    ? (config.type === 'select' ? null : "")
                    : config.defaultValue;
            });
            return clearedLocalValues;
        });

        setActiveFilters(() => {
            const clearedActiveFilters: Record<string, FilterValue | null> = {};
            filterConfigs.forEach(config => {
                if (config.defaultValue !== undefined) {
                    clearedActiveFilters[config.field] = config.defaultValue === null ? null : [config.defaultValue];
                } else {
                    clearedActiveFilters[config.field] = config.field.endsWith('Id') ? [0] : [""];
                }
            });
            return clearedActiveFilters;
        });

        setPagination(prev => ({ ...prev, current: 1 }));
    };


    const handleTableChange: TableProps<T>["onChange"] = (
        newPagination,
        newFilters,
        newSorter
    ) => {
        const didPaginationChange =
            newPagination.current !== pagination.current ||
            newPagination.pageSize !== pagination.pageSize;

        setPagination({
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        });

        if (!Array.isArray(newSorter)) {
            setSorter({
                field: newSorter.field as string | undefined,
                order: newSorter.order,
            });
        } else if (newSorter.length > 0) {
            setSorter({
                field: newSorter[0].field as string | undefined,
                order: newSorter[0].order,
            });
        } else {
            setSorter({});
        }

        setActiveFilters(prevActiveFilters => {
            const mergedFilters = { ...prevActiveFilters };
            Object.entries(newFilters).forEach(([key, value]) => {
                if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
                    delete mergedFilters[key];
                } else {
                    mergedFilters[key] = value;
                }
            });
            return mergedFilters;
        });

        if (didPaginationChange) {
            window.requestAnimationFrame(() => {
                tableContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                if (window.scrollY > 0) {
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                }
            });
        }
    };

    const defaultColumns: ColumnsType<T> =
        data?.items && data?.items.length > 0 && !customColumns
            ? (Object.keys(data?.items[0]) as (keyof T)[])
                .filter((key) => key !== "id")
                .map((key) => ({
                    title: t(`table.${String(key)}`, {
                        defaultValue: String(key).charAt(0).toUpperCase() + String(key).slice(1),
                    }),
                    dataIndex: key as string,
                    key: key as string,
                    sorter: true,
                }))
            : [];

    const actionColumn: ColumnsType<T>[number] = {
        title: t("table.actions", { defaultValue: "Actions" }),
        key: "actions",
        width: actionColumnWidth,
        fixed: 'right',
        render: (_, record) => {
            if (actionVisibility !== undefined) {
                let visible = true;
                if (typeof actionVisibility === 'function') {
                    visible = actionVisibility(record);
                } else {
                    visible = !!actionVisibility;
                }

                if (!visible) {
                    return null;
                }
            }

            let isEditVisible = true;
            let isDeleteVisible = true;
            // let isViewVisible = false;

            // Use effective showActions here
            if (typeof showActions === 'object') {
                // if (showActions.view !== undefined) {
                //     if (typeof showActions.view === 'function') {
                //         isViewVisible = showActions.view(record);
                //     } else {
                //         isViewVisible = !!showActions.view;
                //     }
                // }

                // console.log('showActions.edit', showActions.edit);
                if (showActions.edit !== undefined) {
                    if (typeof showActions.edit === 'string') {
                        isEditVisible = hasPermission(showActions.edit);
                    } else {
                        isEditVisible = typeof showActions.edit === 'function' ? showActions.edit(record) : !!showActions.edit;
                    }
                }
                if (showActions.delete !== undefined) {
                    if (typeof showActions.delete === 'string') {
                        isDeleteVisible = hasPermission(showActions.delete);
                    } else {
                        isDeleteVisible = typeof showActions.delete === 'function' ? showActions.delete(record) : !!showActions.delete;
                    }
                }
            } else if (typeof showActions === 'boolean') {
                isEditVisible = showActions;
                isDeleteVisible = showActions;
            }


            if (isEditVisible) {
                isEditVisible = hasPermission(
                    addNewPermission?.replace(/save/i, "update") || ""
                );
            }

            console.log('isEditVisible', isEditVisible);

            if (isDeleteVisible) {
                isDeleteVisible = hasPermission(getPermissionKeyFromApi(deleteBaseUrl || ""));
            }

            return (
                <Space>
                    {customActions.map((action, index) => (
                        <div key={index}>{action.render(record)}</div>
                    ))}
                    {(onEdit || editBaseUrl) && isEditVisible && (
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => {
                                if (onEdit) {
                                    onEdit(record.id);
                                } else if (editBaseUrl) {
                                    let url;
                                    if (typeof editBaseUrl === "function") {
                                        url = editBaseUrl(record);
                                    } else {
                                        url = editBaseUrl;
                                    }
                                    router.navigate({
                                        to: url,
                                        state: { record: record } as Record<string, unknown>,
                                    });
                                }
                            }}
                            style={{
                                borderRadius: '6px',
                                height: '32px',
                                width: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: `1px solid ${token.colorBorder}`,
                                transition: 'all 0.2s ease',
                                color: token.colorText
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = token.colorPrimary;
                                e.currentTarget.style.color = token.colorPrimary;
                                e.currentTarget.style.backgroundColor = 'var(--app-accent-soft-2)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = token.colorBorder;
                                e.currentTarget.style.color = token.colorText;
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        />
                    )}
                    {deleteBaseUrl && isDeleteVisible && (
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            loading={
                                deleteMutation.status === "pending" &&
                                deleteMutation.variables?.id === record.id
                            }
                            onClick={() => handleDelete(record)}
                            style={{
                                borderRadius: '6px',
                                height: '32px',
                                width: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid var(--app-danger-soft-border)',
                                transition: 'all 0.2s ease',
                                color: 'var(--app-danger-soft-text)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = 'var(--app-danger-soft-text)';
                                e.currentTarget.style.backgroundColor = 'var(--app-danger-soft-bg)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'var(--app-danger-soft-border)';
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        />)}

                </Space>
            );
        },
    };

    const columns: ColumnsType<T> = customColumns
        ? customColumns.map(col => {
            const newCol = { ...col };
            if ('dataIndex' in col && col.dataIndex) {
                const dataIndexString = Array.isArray(col.dataIndex) ? col.dataIndex.join('.') : String(col.dataIndex);
                newCol.title = col.title ? t(`table.${dataIndexString}`, { defaultValue: String(col.title) }) : newCol.title;
            } else if (col.key) {
                newCol.title = col.title ? t(`table.${String(col.key)}`, { defaultValue: String(col.title) }) : newCol.title;
            }
            return newCol;
        })
        : [...defaultColumns];

    if (showActions) {
        columns.push(actionColumn);
    }

    const rowSelection = rowSelectionEnabled
        ? {
            selectedRowKeys: actualSelectedRowKeys,
            onChange: (newSelectedRowKeys: Key[], newSelectedRows: T[]) => { // Changed to Key[]
                if (controlledSelectedRowKeys === undefined) {
                    setInternalSelectedRowKeys(newSelectedRowKeys);
                }
                onRowSelectionChange?.(newSelectedRowKeys, newSelectedRows);
            },
            getCheckboxProps: (record: T) => ({
                disabled: !rowSelectable(record),
            }),
        }
        : undefined;

    return (
        <div ref={tableContainerRef} style={{ padding: '0 2px' }}>
            {error && (
                <div style={{
                    color: 'var(--app-danger-soft-text)',
                    padding: '12px 16px',
                    backgroundColor: 'var(--app-danger-soft-bg)',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    fontSize: '14px',
                    fontWeight: 500,
                    border: '1px solid var(--app-danger-soft-border)'
                }}>
                    ⚠️ {error.message}
                </div>
            )}

            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '24px',
                gap: '16px',
                flexWrap: 'wrap'
            }}>
                {tableTitle && (
                    <div style={{
                        flex: 1,
                        minWidth: '200px'
                    }}>
                        <h1 style={{
                            margin: 0,
                            fontSize: '28px',
                            fontWeight: 700,
                            color: token.colorText,
                            letterSpacing: '-0.5px'
                        }}>
                            {typeof tableTitle === 'string' ? t(tableTitle) : tableTitle}
                        </h1>
                        <p style={{
                            margin: '6px 0 0 0',
                            fontSize: '14px',
                            color: token.colorTextSecondary,
                            fontWeight: 400
                        }}>
                            {data?.total || 0} {t(data?.total === 1 ? 'table.item' : 'table.items')}
                        </p>
                    </div>
                )}
                <Flex gap="small" wrap style={{ justifyContent: 'flex-end' }}>
                    {/* Header Actions */}
                    {headerActions && headerActions.length > 0 && (
                        <Space style={{ marginBottom: 16 }}>
                            {headerActions.map((action, index) => (
                                <React.Fragment key={index}>{action}</React.Fragment>
                            ))}
                        </Space>
                    )}
                    {/* Second Button */}
                    {hasPermission(getPermissionKeyFromApi(secondButtonPermissionKey || "")) && secondButtonTitle && secondButtonUrl && (
                        <Button
                            type="default"
                            onClick={() => router.navigate({ to: secondButtonUrl })}
                            style={{ marginBottom: 16 }}
                        >
                            {t(secondButtonTitle)}
                        </Button>
                    )}
                    {(addNewPermission ? hasPermission(addNewPermission) : (typeof showActions === 'boolean' ? showActions : false)) && editBaseUrl && (
                        showAddNew && (<Button
                            type="primary"
                            onClick={() => router.navigate({ to: 'form' })}
                            style={{ marginBottom: 16 }}
                        >
                            {t(addNewButtonText || "Add New")}
                        </Button>)
                    )}
                    {/* Export Button */}
                    {filterConfigs.some(data => data.type === 'export') &&
                        filterConfigs
                            .filter(data => data.type === 'export')
                            .map((config, index) => (
                                hasPermission(getPermissionKeyFromApi(config.exportOptions!.url)) &&
                                <Export
                                    key={index}
                                    fileName={config.exportOptions?.fileName}
                                    fileType={config.exportOptions?.fileType?.toLowerCase() === "excel" ? "excel" : "csv"}
                                    apiUrl={config.exportOptions!.url}
                                    defaultPayload={config.exportOptions?.defaultPayload}
                                    extraPayload={buildExtraPayload()}
                                    columns={config.exportOptions?.columns}
                                />
                            ))
                    }
                </Flex>
            </div>
            {/* Filter Inputs and Global Search/Clear Buttons */}
            {filterConfigs.length > 0 && (
                <div style={{
                    backgroundColor: token.colorFillAlter,
                    borderRadius: '12px',
                    padding: '16px 20px',
                    marginBottom: '24px',
                    border: `1px solid ${token.colorBorder}`,
                    display: 'flex',
                    gap: '12px',
                    flexWrap: 'wrap',
                    alignItems: 'center'
                }}>
                    <span style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: token.colorTextSecondary,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginRight: '4px'
                    }}>
                        🔍 {t('table.filter')}
                    </span>
                    {filterConfigs.map((config) => {
                        const translatedPlaceholder = config.placeholder ? t(`table.placeholder.${config.placeholder}`, { defaultValue: config.placeholder }) : t('table.placeholder.search', { field: config.placeholder });
                        if (config.type === 'switch') {
                            return (
                                <Space key={config.field} style={{
                                    padding: '8px 12px',
                                    backgroundColor: token.colorBgContainer,
                                    borderRadius: '8px',
                                    border: `1px solid ${token.colorBorder}`,
                                    display: 'flex'
                                }}>
                                    <span style={{ fontSize: '13px', fontWeight: 500, color: token.colorTextSecondary }}>{translatedPlaceholder}</span>
                                    <Switch
                                        checked={!!localInputValues[config.field]}
                                        onChange={(checked) => {
                                            setLocalInputValues(prev => ({
                                                ...prev,
                                                [config.field]: checked ? 1 : 0,
                                            }));
                                        }}
                                    />
                                </Space>
                            );
                        }
                        if (config.type === 'select') {
                            return (
                                <Select
                                    key={config.field}
                                    style={{ minWidth: 200 }}
                                    size="large"
                                    placeholder={translatedPlaceholder}
                                    allowClear
                                    showSearch={config.showSearch}
                                    value={localInputValues[config.field]}
                                    onChange={(value) => {
                                        setLocalInputValues(prev => ({
                                            ...prev,
                                            [config.field]: value,
                                        }));
                                    }}
                                    options={config.options || dropdownOptions[config.field] || []}
                                    filterOption={config.showSearch ? (input, option) => {
                                        const label = typeof option?.label === 'string' ? option.label : '';
                                        return label.toLowerCase().includes(input.toLowerCase());
                                    } : undefined}
                                />
                            );
                        }
                        if (config.type === 'date') {
                            const dateDisplayFormat = config.displayFormat || 'DD-MM-YYYY';
                            return (
                                <DatePicker
                                    size="large"
                                    key={config.field}
                                    style={{ width: 200 }}
                                    placeholder={translatedPlaceholder}
                                    format={dateDisplayFormat}
                                    value={localInputValues[config.field] ? dayjs(localInputValues[config.field] as string, 'YYYY-MM-DD') : null}
                                    onChange={(_date, dateString) => {
                                        // Convert from display format back to YYYY-MM-DD for internal storage
                                        let formattedDate = "";
                                        if (_date) {
                                            formattedDate = _date.format('YYYY-MM-DD');
                                        }
                                        setLocalInputValues(prev => ({
                                            ...prev,
                                            [config.field]: formattedDate,
                                        }));
                                    }}
                                />
                            );
                        }
                        if (config.type === "export") {
                            return;
                        }
                        return (
                            <Input
                                key={config.field}
                                placeholder={translatedPlaceholder}
                                allowClear
                                value={String(localInputValues[config.field] ?? '')}
                                onChange={(e) => {
                                    setLocalInputValues(prev => ({
                                        ...prev,
                                        [config.field]: e.target.value,
                                    }));
                                }}
                                onPressEnter={handleGlobalSearch}
                                style={{ width: 200 }}
                            />
                        );
                    })}
                    <Button
                        type="primary"
                        icon={<SearchOutlined />}
                        onClick={handleGlobalSearch}
                        style={{
                            borderRadius: '8px',
                            fontWeight: 600,
                            height: '36px'
                        }}
                    >
                    </Button>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={handleClearFilters}
                        style={{
                            borderRadius: '8px',
                            fontWeight: 600,
                            height: '36px',
                            border: '1px solid #E2E8F0'
                        }}
                    >
                    </Button>
                </div>
            )}

            {isLoading || isFetching ? (
                <div style={{ display: 'grid', gap: 12, width: '100%' }}>
                    {Array.from({ length: pagination.pageSize || 10 }).map((_, idx) => (
                        <Skeleton key={idx} active paragraph={{ rows: 1, width: '100%' }} style={{ margin: '12px 0', width: '100%' }} />
                    ))}
                </div>
            ) : (data?.items && data?.items.length > 0) ? (
                <Table<T>
                    rowKey="id"
                    expandable={expandable}
                    loading={false}
                    dataSource={data?.items || []}
                    columns={columns}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: data?.total ?? 0,
                        showSizeChanger: true,
                        showTotal: (total, range) => t("table.pagination.showTotal", {
                            start: range[0],
                            end: range[1],
                            total,
                            defaultValue: `${range[0]}-${range[1]} of ${total} items`,
                        }),
                        style: { marginTop: '16px' }
                    }}
                    onChange={handleTableChange}
                    rowSelection={rowSelection}
                    scroll={{ x: 'max-content' }}
                    tableLayout="auto"
                    bordered
                    className="rounded-lg shadow-md"
                    rowClassName={() => 'hover:bg-blue-50 transition-colors'}
                    onRow={() => ({
                        style: {
                            transition: 'background-color 0.2s ease',
                            cursor: 'pointer'
                        }
                    })}
                />
            ) : (
                <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    backgroundColor: '#F8FAFC',
                    borderRadius: '12px',
                    border: '2px dashed #E2E8F0'
                }}>
                    <div style={{
                        fontSize: '48px',
                        marginBottom: '16px',
                        opacity: 0.5
                    }}>
                        📋
                    </div>
                    <h3 style={{
                        fontSize: '18px',
                        fontWeight: 600,
                        color: '#0F172A',
                        margin: '0 0 8px 0'
                    }}>
                        {t("table.noData", { defaultValue: "No data available" })}
                    </h3>
                    <p style={{
                        fontSize: '14px',
                        color: '#64748B',
                        margin: '0 0 20px 0',
                        maxWidth: '400px',
                        marginLeft: 'auto',
                        marginRight: 'auto'
                    }}>
                        {t("table.noDataDescription", { defaultValue: "Try adjusting your filters or adding new items to get started." })}
                    </p>
                    {/*{(addNewPermission ? hasPermission(addNewPermission) : (typeof showActions === 'boolean' ? showActions : false)) && editBaseUrl && showAddNew && (*/}
                    {/*    <Button*/}
                    {/*        type="primary"*/}
                    {/*        onClick={() => router.navigate({ to: editBaseUrl })}*/}
                    {/*        size="large"*/}
                    {/*        style={{ borderRadius: '8px' }}*/}
                    {/*    >*/}
                    {/*        {t(addNewButtonText || "Add New Item")}*/}
                    {/*    </Button>*/}
                    {/*)}*/}
                </div>
            )}

            {/* Enhanced Delete Confirmation Modal */}
            <Modal
                open={deleteModalOpen}
                onCancel={() => setDeleteModalOpen(false)}
                footer={null}
                closable={false}
                width={clamp(90, 420, 520)}
                style={{ backdropFilter: 'blur(4px)' }}
                bodyStyle={{ padding: '40px 32px' }}
            >
                <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
                    {/* Icon */}
                    <div
                        style={{
                            width: 72,
                            height: 72,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #EF444420 0%, #DC262620 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 32,
                            border: '2px solid #FEE2E2'
                        }}
                    >
                        <ExclamationCircleOutlined style={{ color: '#DC2626' }} />
                    </div>

                    {/* Title */}
                    <div>
                        <h2 style={{ margin: '0 0 8px 0', fontSize: 20, fontWeight: 700, color: '#0F172A', letterSpacing: '-0.3px' }}>
                            {t("delete.confirm.title", { defaultValue: "Delete Item" })}
                        </h2>
                        <p style={{ margin: 0, fontSize: 14, color: '#64748B', fontWeight: 400, lineHeight: 1.5 }}>
                            {t("delete.confirm.message", { defaultValue: "This action cannot be undone. This will permanently delete the item from the system." })}
                        </p>
                    </div>

                    {/* Action Buttons */}
                    <Space style={{ marginTop: 16, width: '100%', justifyContent: 'center' }}>
                        <Button
                            onClick={() => setDeleteModalOpen(false)}
                            style={{
                                borderRadius: 10,
                                padding: '10px 24px',
                                fontSize: 14,
                                fontWeight: 600,
                                height: 40,
                                minWidth: 120
                            }}
                        >
                            {t("common.cancel", { defaultValue: "Cancel" })}
                        </Button>
                        <Button
                            type="primary"
                            danger
                            onClick={() => {
                                if (deleteTarget) {
                                    deleteMutation.mutate(deleteTarget);
                                    setDeleteModalOpen(false);
                                }
                            }}
                            loading={deleteMutation.status === 'pending'}
                            style={{
                                borderRadius: 10,
                                padding: '10px 24px',
                                fontSize: 14,
                                fontWeight: 600,
                                height: 40,
                                minWidth: 120,
                                background: '#DC2626',
                                borderColor: '#DC2626'
                            }}
                        >
                            {t("common.delete", { defaultValue: "Delete" })}
                        </Button>
                    </Space>
                </div>
            </Modal>

            {/* Success Modal after Delete */}
            <Modal
                open={successModalOpen}
                onCancel={() => setSuccessModalOpen(false)}
                footer={null}
                closable={false}
                width={clamp(90, 420, 520)}
                style={{ backdropFilter: 'blur(4px)' }}
                bodyStyle={{ padding: '40px 32px' }}
            >
                <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
                    {/* Icon */}
                    <div
                        style={{
                            width: 72,
                            height: 72,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #10B98120 0%, #05937720 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 32,
                            border: '2px solid #D1FAE5'
                        }}
                    >
                        <CheckCircleOutlined style={{ color: '#059377' }} />
                    </div>

                    {/* Title */}
                    <div>
                        <h2 style={{ margin: '0 0 8px 0', fontSize: 20, fontWeight: 700, color: '#0F172A', letterSpacing: '-0.3px' }}>
                            {t('common.deletedSuccessfully', { defaultValue: 'Deleted Successfully' })}
                        </h2>
                        <p style={{ margin: 0, fontSize: 14, color: '#64748B', fontWeight: 400, lineHeight: 1.5 }}>
                            {successMessage}
                        </p>
                    </div>

                    {/* Action Button */}
                    <Button
                        type="primary"
                        onClick={() => setSuccessModalOpen(false)}
                        style={{
                            borderRadius: 10,
                            padding: '10px 32px',
                            fontSize: 14,
                            fontWeight: 600,
                            height: 40,
                            minWidth: 140,
                            background: '#059377',
                            borderColor: '#059377'
                        }}
                    >
                        {t('common.close', { defaultValue: 'Close' })}
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

function clamp(min: number, val: number, max: number) {
    return Math.min(Math.max(val, min), max);
}

export default DynamicTable;